/**
 * ChatUploadCard — inline PDF upload + extraction status card.
 *
 * Lives below the assistant message whenever `store.uploadPrompt` (driven by
 * the `upload_prompt` SSE event from a `request_upload` tool call) or
 * `store.inflightUpload` (set after the file POST returns) is non-null.
 *
 * Four visual sub-states, all rendered in the same outer container:
 *   1. idle / drag-over — compact dropzone, click to browse OR drop a PDF
 *   2. uploading        — file POST in flight, spinner + filename
 *   3. extracting       — polls useExtractionStatus, mirrors the
 *                         ExtractionPage progress UI (stages + cycling
 *                         messages). The user keeps full chat input access
 *                         while this runs.
 *   4. error            — upload POST failed or extraction terminated in
 *                         a failed state. Try-again button retries the
 *                         relevant step.
 *
 * Lifecycle:
 *   - Synthetic resume message is sent only after extraction reaches a
 *     terminal complete status (ANALYZED). This keeps the chat stream
 *     free during upload + extraction so the user can chat about other
 *     things in parallel.
 *   - On terminal complete: fire synthetic → clearUploadPrompt() (which
 *     also clears inflightUpload). Card unmounts.
 *   - On terminal failure: card stays mounted with error UI; user can
 *     retry or continue via the dedicated /extraction page.
 *
 * Brand book: no rounded corners, semantic tokens (text-error, text-orange,
 * surface-card), Feather icons, sentence case copy.
 */
import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  FileText,
  Loader,
  RefreshCw,
  Upload,
} from 'react-feather';

import { api } from '../../../api/client';
import { useExtractionStatus, useReprocessTermSheet } from '../../../api/hooks/useTermSheets';
import { Badge, ProgressBar } from '../../../components/ui';
import {
  COMPLETE_STATUSES,
  FAILED_STATUSES,
  getProgressStages,
  getStatusConfig,
} from '../../extraction/lib/extractionStatus';
import { useChatStore } from '../../../stores/chatStore';
import { useUIStore } from '../../../stores/uiStore';
import { useChatStream } from '../hooks/useChatStream';

type UploadState = 'idle' | 'uploading' | 'error';

const MAX_SIZE_MB = 25;

function isPdf(file: File): boolean {
  return (
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf')
  );
}

function formatBytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ChatUploadCard() {
  const uploadPrompt = useChatStore((s) => s.uploadPrompt);
  const inflightUpload = useChatStore((s) => s.inflightUpload);
  const clearUploadPrompt = useChatStore((s) => s.clearUploadPrompt);
  const setInflightUpload = useChatStore((s) => s.setInflightUpload);
  const showToast = useUIStore((s) => s.showToast);
  const { send } = useChatStream();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<{ name: string; size: number } | null>(null);

  // Counter-based drag tracking handles bubbling cleanly: crossing into a
  // child fires `dragleave` on the parent and `dragenter` on the child —
  // naive setState would flicker the dropzone style.
  const dragDepthRef = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  // Always start polling once inflightUpload is set; the hook auto-stops
  // when the status reaches a terminal value.
  const { data: extractionStatus } = useExtractionStatus(
    inflightUpload?.termSheetId ?? 0,
    { enabled: !!inflightUpload },
  );
  const reprocessMutation = useReprocessTermSheet();

  // ── Once-firing completion effect ───────────────────────────────────
  // When extraction reaches a terminal complete status, fire the synthetic
  // resume message and dismiss the card. Ref-guarded so we don't re-fire
  // on the next poll tick (status stays ANALYZED for many seconds).
  const completionFiredRef = useRef(false);
  useEffect(() => {
    if (!inflightUpload) {
      completionFiredRef.current = false;
      return;
    }
    const status = extractionStatus?.status;
    if (!status || !COMPLETE_STATUSES.has(status)) {return;}
    if (completionFiredRef.current) {return;}
    completionFiredRef.current = true;

    const syntheticText =
      `Term sheet "${inflightUpload.fileName}" extracted and analyzed ` +
      `(term_sheet_id: ${inflightUpload.termSheetId}, deal_id: ${inflightUpload.dealId}). ` +
      `Summarize the key terms (margin, fees, term, covenants) for the user.`;

    send({
      text: syntheticText,
      synthetic: true,
      // Authoritative deal/term-sheet context — backend uses these ids
      // even if `dealContext` drifted while extraction was running. Closes
      // the cross-deal leak vector at the schema level: even if the body's
      // `deal_id` is stale, the synthetic_action's ids are the ground truth.
      syntheticAction: {
        kind: 'term_sheet_uploaded',
        deal_id: inflightUpload.dealId,
        term_sheet_id: inflightUpload.termSheetId,
      },
    });
    clearUploadPrompt();
  }, [extractionStatus?.status, inflightUpload, send, clearUploadPrompt]);

  // Card has to render even without uploadPrompt if inflightUpload is still
  // active (e.g. uploadPrompt was cleared but extraction is finishing up).
  // Parent gates on (uploadPrompt || inflightUpload) so this is mostly a
  // belt-and-braces guard.
  if (!uploadPrompt && !inflightUpload) {return null;}

  // Authoritative deal id: prefer the inflight pointer once we have one,
  // otherwise the prompt's target.
  const dealId = inflightUpload?.dealId ?? uploadPrompt?.deal_id ?? null;
  const label = uploadPrompt?.label ?? 'Upload your term sheet';

  // ── Drag-and-drop bindings ──────────────────────────────────────────
  // Only active in the idle/uploading-eligible window. Once inflightUpload
  // is set, we don't accept new drops on this card.
  const acceptsDrop =
    !inflightUpload && uploadState !== 'uploading' && !!uploadPrompt;

  const handleDragEnter = (e: React.DragEvent) => {
    if (!acceptsDrop) {return;}
    if (!Array.from(e.dataTransfer.types).includes('Files')) {return;}
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!acceptsDrop) {return;}
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!acceptsDrop) {return;}
    if (!Array.from(e.dataTransfer.types).includes('Files')) {return;}
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {setIsDragOver(false);}
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!acceptsDrop) {return;}
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) {return;}
    const file = files[0];
    if (files.length > 1) {
      showToast(
        `Multiple files dropped — uploading only "${file.name}".`,
        'info',
      );
    }
    void uploadFile(file);
  };

  // ── File picker path ────────────────────────────────────────────────
  const openFilePicker = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {return;}
    // Reset so picking the same file twice still fires onChange.
    if (fileInputRef.current) {fileInputRef.current.value = '';}
    void uploadFile(file);
  };

  // ── Upload + handoff to extraction polling ──────────────────────────
  const uploadFile = async (file: File) => {
    if (!isPdf(file)) {
      showToast('Only PDF files are supported.', 'error');
      return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_SIZE_MB) {
      showToast(`File is too large (max ${MAX_SIZE_MB} MB).`, 'error');
      return;
    }
    if (!dealId) {
      showToast('Open a deal first to upload a term sheet.', 'warning');
      return;
    }

    setSelectedFile({ name: file.name, size: file.size });
    setUploadState('uploading');
    setErrorMessage(null);

    try {
      const termSheet = await api.termSheets.upload(dealId, file);

      // Defense in depth: the upload card's deal_id (from the SSE event)
      // is the authoritative target. If chatStore.dealContext points at a
      // different deal (e.g. stale from a prior session), realign it so
      // the synthetic POST /chat we send on completion carries the
      // upload's deal_id.
      const store = useChatStore.getState();
      if (store.dealContext?.deal_id !== dealId) {
        store.setDealContext({
          deal_id: dealId,
          deal_name: store.dealContext?.deal_name,
        });
      }

      // Hand off to extraction polling. The card stays mounted; the next
      // render flips to the extracting sub-state.
      setInflightUpload({
        termSheetId: termSheet.id,
        fileName: file.name,
        dealId,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setErrorMessage(message);
      setUploadState('error');
    }
  };

  // ── Extraction error retry ──────────────────────────────────────────
  const handleExtractionRetry = () => {
    if (!inflightUpload) {return;}
    reprocessMutation.mutate({ termSheetId: inflightUpload.termSheetId });
  };

  const handleUploadRetry = () => {
    setUploadState('idle');
    setErrorMessage(null);
    setSelectedFile(null);
    openFilePicker();
  };

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────

  // Sub-state: extraction in flight or terminal.
  if (inflightUpload) {
    const status = extractionStatus?.status ?? 'UPLOADED';
    const config = getStatusConfig(status);
    const stages = getProgressStages(status);
    const isFailed = FAILED_STATUSES.has(status);

    return (
      <div className="border border-border bg-surface-card px-3 py-3">
        <div className="flex items-center gap-2">
          {isFailed ? (
            <AlertCircle size={16} className="flex-shrink-0 text-error" />
          ) : COMPLETE_STATUSES.has(status) ? (
            <CheckCircle size={16} className="flex-shrink-0 text-success" />
          ) : (
            <FileText size={16} className="flex-shrink-0 text-orange" />
          )}
          <span
            className="min-w-0 flex-1 truncate text-body-sm font-medium text-txt-primary"
            title={inflightUpload.fileName}
          >
            {inflightUpload.fileName}
          </span>
          <Badge variant={config.color}>{config.label}</Badge>
        </div>

        {!isFailed && (
          <div className="mt-3">
            <ProgressBar
              indeterminate={!COMPLETE_STATUSES.has(status)}
              value={COMPLETE_STATUSES.has(status) ? 100 : 0}
              stages={stages}
            />
          </div>
        )}

        <p className="mt-2 text-caption text-txt-secondary">{config.message}</p>

        {isFailed && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExtractionRetry}
              disabled={reprocessMutation.isPending}
              className="inline-flex items-center gap-1.5 border border-border bg-surface-card px-2.5 py-1 text-caption font-medium text-txt-primary hover:bg-surface-subtle disabled:opacity-50"
            >
              <RefreshCw
                size={12}
                className={reprocessMutation.isPending ? 'animate-spin' : ''}
              />
              {reprocessMutation.isPending ? 'Retrying…' : 'Try again'}
            </button>
            <span className="text-caption text-txt-tertiary">
              You can also continue this from the term sheet page in the main app.
            </span>
          </div>
        )}
      </div>
    );
  }

  // Sub-state: upload POST in flight.
  if (uploadState === 'uploading' && selectedFile) {
    return (
      <div className="border border-border bg-surface-card px-3 py-3">
        <div className="flex items-center gap-2">
          <Loader size={14} className="flex-shrink-0 animate-spin text-orange" />
          <span
            className="min-w-0 flex-1 truncate text-body-sm font-medium text-txt-primary"
            title={selectedFile.name}
          >
            {selectedFile.name}
          </span>
          <span className="flex-shrink-0 text-caption text-txt-tertiary">
            {formatBytes(selectedFile.size)}
          </span>
        </div>
        <p className="mt-2 text-caption text-txt-secondary">Uploading…</p>
      </div>
    );
  }

  // Sub-state: upload error (POST failed).
  if (uploadState === 'error') {
    return (
      <div className="border border-border bg-surface-card px-3 py-3">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-error" />
          <div className="min-w-0 flex-1">
            <p className="text-body-sm text-txt-primary">
              {errorMessage ?? 'Upload failed.'}
            </p>
            <button
              type="button"
              onClick={handleUploadRetry}
              className="mt-1 text-caption text-orange hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sub-state: idle / drag-over — the polished dropzone.
  return (
    <div className="border border-border bg-surface-card">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        onChange={handleFileChange}
        aria-label={label}
      />

      <button
        type="button"
        onClick={openFilePicker}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-label={label}
        className={[
          'flex w-full flex-col items-center justify-center gap-1 px-3 py-4 text-center',
          'border-2 border-dashed transition-colors duration-150',
          isDragOver
            ? 'border-orange bg-orange/5'
            : 'border-border hover:border-border-strong hover:bg-surface-subtle',
        ].join(' ')}
      >
        <Upload
          size={18}
          className={isDragOver ? 'text-orange' : 'text-txt-tertiary'}
        />
        <span className="text-body-sm font-medium text-txt-primary">
          {isDragOver
            ? 'Release to upload'
            : 'Drop your term sheet here or click to browse'}
        </span>
        <span className="text-caption text-txt-tertiary">
          PDF up to {MAX_SIZE_MB} MB
        </span>
      </button>
    </div>
  );
}
